// « Enregistrement retrouvé » sur l'accueil (anti-perte phase 1, A9, spec
// §5.2.4) : groupes de fichiers audio restés sur le téléphone sans capture
// complète côté serveur (orphelins des anciennes versions, dossiers de prise au
// manifeste illisible, capture tronquée du 24-09).
//
// - Lecture : les captures de la PERSONNE (filtre `created_by` explicite — un
//   admin lit aussi les captures partagées des autres et s'apparierait à la
//   capture d'autrui), 15 derniers jours, total du `content-range` vérifié
//   (plafond PostgREST de 1 000 lignes, troncature SILENCIEUSE). Une photo
//   incomplète ne propose rien : un groupe apparié à une capture manquante
//   serait pris pour un nouvel enregistrement, donc doublé.
// - Repérage : `findRecordingsToFinish` (A6, pure), avec les fichiers de la
//   trace `pending-recording` (audio finalisé jamais rattaché, 2026-08-17 :
//   il n'est plus offert par l'écran de capture, il doit l'être ici).
// - Publication : un petit magasin en mémoire que la carte d'accueil lit ; le
//   filet global (`use-resync-pending`) le remplit, la carte en retire ce
//   qu'elle a traité.
// - Gestes : « Sauvegarder l'enregistrement » adopte le groupe et laisse
//   l'exécuteur faire le reste (projet choisi ou capture à compléter gardés
//   dans `take-overrides.ts`) ; « Mettre à la corbeille » l'adopte puis le met
//   à la corbeille (7 jours), avec la même notice qu'un abandon.
import type { SupabaseClient } from "@supabase/supabase-js";
import type { TakeRecorder } from "../native/take-recorder";
import { findRecordingsToFinish, type ServerCaptureLite, type ToFinish } from "./orphans";
import type { PendingRecordingMeta, PendingRecordingStore } from "./pending-recording";
import type { CaptureType } from "./schema";
import { fallbackTakeType, type TakeOverrideStore } from "./take-overrides";
import { TAKES_CHANGED_EVENT } from "./take-runner";
import type { LegacyGroupInfo, TakeMeta } from "./take-types";
import { TRASH_CHANGED_EVENT, type TrashMetaStore } from "./trash";

/** Fenêtre de lecture des captures (la copie serveur de l'audio est purgée à 14 jours). */
export const OWN_CAPTURES_WINDOW_MS = 15 * 86_400_000;
const PAGE_SIZE = 500;
// Garde contre une boucle sans fin (15 jours de captures d'une personne
// tiennent très loin sous ce plafond) ; l'atteindre ⇒ photo incomplète ⇒ null.
const MAX_PAGES = 10;

export interface OwnCaptureLite extends ServerCaptureLite {
  projectId: string;
  type: CaptureType;
}

interface OwnCaptureRow {
  id: string;
  recorded_at: string;
  duration_s: number | null;
  status: string;
  project_id: string;
  type: CaptureType;
}

/**
 * Captures de la personne des 15 derniers jours ; `null` si la lecture échoue
 * ou si la photo est incomplète (moins de lignes que le total annoncé).
 */
export async function fetchOwnRecentCaptures(
  client: Pick<SupabaseClient, "from">,
  userId: string,
  nowMs: number,
): Promise<OwnCaptureLite[] | null> {
  const since = new Date(nowMs - OWN_CAPTURES_WINDOW_MS).toISOString();
  const rows: OwnCaptureRow[] = [];
  let total: number | null = null;
  for (let page = 0; page < MAX_PAGES; page++) {
    const from = page * PAGE_SIZE;
    const { data, error, count } = await client
      .from("captures")
      .select("id, recorded_at, duration_s, status, project_id, type", { count: "exact" })
      .eq("created_by", userId)
      .gte("recorded_at", since)
      .order("recorded_at", { ascending: false })
      .range(from, from + PAGE_SIZE - 1);
    if (error || !data) return null;
    if (typeof count === "number") total = count;
    rows.push(...(data as OwnCaptureRow[]));
    if (data.length < PAGE_SIZE || (total !== null && rows.length >= total)) break;
  }
  if (total === null || rows.length < total) return null;
  return rows.map((r) => ({
    id: r.id,
    // ISO brut, tel que PostgREST le rend : `findRecordingsToFinish` le lit
    // avec `new Date`, aucune remise en forme (fuseau) entre les deux.
    recordedAt: r.recorded_at,
    durationS: r.duration_s,
    status: r.status,
    projectId: r.project_id,
    type: r.type,
  }));
}

function fileNameOf(uri: string): string {
  return uri.split("/").pop() ?? uri;
}

/** Noms des fichiers audio que la trace `pending-recording` attend encore de rattacher. */
export function pendingFileNames(pending: PendingRecordingMeta[]): Set<string> {
  const names = new Set<string>();
  for (const p of pending) if (p.finalizedUri) names.add(fileNameOf(p.finalizedUri));
  return names;
}

export interface RecordingToFinish {
  item: ToFinish;
  /** Trace du fichier dans `pending-recording` (type, titre, projet de départ), si elle existe. */
  pending: PendingRecordingMeta | null;
  /** Capture existante à compléter (C25) : son projet et son type. */
  capture: { projectId: string; type: CaptureType } | null;
}

function describe(
  groups: LegacyGroupInfo[],
  captures: OwnCaptureLite[],
  pending: PendingRecordingMeta[],
  nowMs: number,
): RecordingToFinish[] {
  const byGroup = new Map(groups.map((g) => [g.groupId, g]));
  const byCapture = new Map(captures.map((c) => [c.id, c]));
  const found = findRecordingsToFinish(groups, captures, pendingFileNames(pending), nowMs);
  // R66 (revue finale A-I5) : compléter une capture DÉJÀ analysée changerait
  // son audio sans relancer son analyse (le serveur ne re-transcrit pas une
  // capture transcrite, R61) — la fiche montrerait 1 h 29 min d'audio et
  // l'analyse de 30 s. Aucune carte : l'audio reste sur le téléphone, rien
  // n'est perdu, jusqu'à la re-transcription côté serveur (suivi).
  const proposable = found.filter((item) => !(item.kind === "complete" && item.alreadyAnalyzed));
  return proposable.map((item) => {
    const files = new Set(byGroup.get(item.groupId)?.files ?? []);
    const entry =
      pending.find((p) => p.finalizedUri && files.has(fileNameOf(p.finalizedUri))) ?? null;
    const c = item.kind === "complete" ? byCapture.get(item.captureId) : undefined;
    return {
      item,
      pending: entry,
      capture: c ? { projectId: c.projectId, type: c.type } : null,
    };
  });
}

// ─── Magasin en mémoire lu par la carte d'accueil ─────────────────────────────

let current: RecordingToFinish[] = [];
const listeners = new Set<() => void>();

export function publishRecordingsToFinish(list: RecordingToFinish[]): void {
  current = list;
  for (const l of listeners) l();
}

export function subscribeRecordingsToFinish(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function recordingsToFinishSnapshot(): RecordingToFinish[] {
  return current;
}

function forgetRecordingToFinish(groupId: string): void {
  publishRecordingsToFinish(current.filter((r) => r.item.groupId !== groupId));
}

export interface RefreshDeps {
  listTakes: TakeRecorder["listTakes"];
  /** `null` : lecture en échec ou photo incomplète — rien n'est republié. */
  fetchCaptures: () => Promise<OwnCaptureLite[] | null>;
  pending: () => Promise<PendingRecordingMeta[]>;
  nowMs: number;
}

/** Repère les enregistrements à proposer et les publie pour l'accueil. */
export async function refreshRecordingsToFinish(d: RefreshDeps): Promise<void> {
  const [{ legacyGroups }, captures, pending] = await Promise.all([
    d.listTakes(),
    d.fetchCaptures(),
    d.pending(),
  ]);
  if (captures === null) return;
  publishRecordingsToFinish(describe(legacyGroups, captures, pending, d.nowMs));
}

// ─── Gestes de la carte ──────────────────────────────────────────────────────

export interface RecordingActionDeps {
  recorder: Pick<TakeRecorder, "adoptOrphans" | "markTake" | "trashTake">;
  overrides: Pick<TakeOverrideStore, "set">;
  pendingStore: Pick<PendingRecordingStore, "release">;
  trashMeta: Pick<TrashMetaStore, "remember">;
  userId: string;
  now: () => Date;
  requestRun: () => void;
}

function metaFor(r: RecordingToFinish, userId: string, projectId: string | null): TakeMeta {
  if (r.capture) {
    return {
      userId,
      type: r.capture.type,
      projectId: r.capture.projectId,
      title: null,
      meetingContext: null,
      batchExtraction: true,
      startedAt: r.item.startedAt,
    };
  }
  const p = r.pending;
  return {
    userId,
    // Aucune trace : type déduit de la durée du groupe (R62).
    type: p?.type ?? fallbackTakeType(r.item.totalMs),
    projectId: projectId ?? p?.projectId ?? null,
    title: p?.title ?? null,
    meetingContext: p?.meetingContext ?? null,
    // Mode d'extraction par défaut de l'écran de capture (« pas pressé »).
    batchExtraction: p?.batchExtraction ?? true,
    startedAt: p?.startedAt ?? r.item.startedAt,
  };
}

// Le fichier a quitté la racine (adopté dans une prise) : sa trace
// `pending-recording` pointerait vers un fichier disparu. On n'acquitte que
// l'entrée de CE fichier (jamais la prise en cours d'une autre).
async function releasePending(d: RecordingActionDeps, r: RecordingToFinish): Promise<void> {
  const uri = r.pending?.finalizedUri;
  if (!uri) return;
  try {
    await d.pendingStore.release(uri);
  } catch (err) {
    console.warn(`[recordings-to-finish] trace de ${uri} non acquittée`, err);
  }
}

function notifyTakesChanged(): void {
  window.dispatchEvent(new Event(TAKES_CHANGED_EVENT));
}

/**
 * « Sauvegarder l'enregistrement » : adopte le groupe dans une prise, garde le
 * projet choisi (ou la capture à compléter, C25) pour cette prise, et relance
 * l'exécuteur. `false` si le natif refuse l'adoption (rien n'a bougé).
 */
export async function finishRecording(
  d: RecordingActionDeps,
  r: RecordingToFinish,
  projectId: string | null,
): Promise<boolean> {
  let takeId: string;
  try {
    takeId = (await d.recorder.adoptOrphans(r.item.groupId)).takeId;
  } catch (err) {
    console.warn(`[recordings-to-finish] groupe ${r.item.groupId} non adopté`, err);
    return false;
  }
  const meta = metaFor(r, d.userId, projectId);
  const item = r.item;
  try {
    if (item.kind === "complete") {
      await d.overrides.set({ takeId, meta, complete: { captureId: item.captureId } });
      // Le manifeste porte aussi l'identifiant de la capture existante. Un
      // échec ici ne change rien : le choix ci-dessus l'impose à l'exécuteur.
      await d.recorder.markTake({ takeId, captureId: item.captureId }).catch(() => undefined);
    } else {
      await d.overrides.set({ takeId, meta });
    }
  } catch (err) {
    // Choix non gardé : la prise existe, sans projet — l'accueil la montrera
    // « En préparation » avec le sélecteur de projet (C13). Rien n'est perdu.
    console.warn(`[recordings-to-finish] choix de la prise ${takeId} non gardé`, err);
  }
  await releasePending(d, r);
  forgetRecordingToFinish(r.item.groupId);
  notifyTakesChanged();
  d.requestRun();
  return true;
}

function trashTakeMemory(r: RecordingToFinish, userId: string): PendingRecordingMeta | null {
  if (r.pending) return { ...r.pending, finalizedUri: null, finalizedDurationMs: null };
  if (!r.capture) return null;
  const meta = metaFor(r, userId, null);
  return {
    startedAt: meta.startedAt,
    projectId: meta.projectId,
    type: meta.type,
    title: null,
    meetingContext: null,
    batchExtraction: meta.batchExtraction,
    userId,
    finalizedUri: null,
    finalizedDurationMs: null,
  };
}

/**
 * « Mettre à la corbeille » : adopte le groupe puis met la prise à la
 * corbeille (7 jours, rien détruit) et l'annonce comme un abandon
 * (`TrashNotice`). `false` si le natif refuse (rien n'a bougé ou la prise
 * reste dans `Takes/`, visible « En préparation »).
 */
export async function trashRecording(
  d: RecordingActionDeps,
  r: RecordingToFinish,
): Promise<boolean> {
  try {
    const { takeId } = await d.recorder.adoptOrphans(r.item.groupId);
    const trashed = await d.recorder.trashTake(takeId);
    await d.trashMeta.remember({
      trashId: trashed.trashId,
      trashedAt: d.now().toISOString(),
      // Le natif ne mesure pas les segments d'une prise adoptée : la durée du
      // groupe (mesurée au repérage) fait foi, jamais « 0 seconde ».
      durationMs: Math.max(trashed.durationMs, r.item.totalMs),
      take: trashTakeMemory(r, d.userId),
      seenAt: null,
    });
    window.dispatchEvent(new Event(TRASH_CHANGED_EVENT));
  } catch (err) {
    console.warn(`[recordings-to-finish] groupe ${r.item.groupId} non mis à la corbeille`, err);
    notifyTakesChanged();
    return false;
  }
  await releasePending(d, r);
  forgetRecordingToFinish(r.item.groupId);
  notifyTakesChanged();
  return true;
}
